// IMPORTAÇÃO DAS BIBLIOTECAS----------------------------------------------------------------------
import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';



// IMPORTAÇÃO DOS ICONES---------------------------------------------------------------------------
import { IoEllipsisHorizontal, IoReload, IoAddCircle, IoClose } from "react-icons/io5";
import { FiTrash, FiAlertCircle, FiEdit, FiMaximize } from "react-icons/fi";
import { GoArchive } from "react-icons/go";
import { MdOutlineSaveAs } from "react-icons/md";
import { ClipLoader } from "react-spinners";



// IMPORTAÇÃO DOS COMPONENTES E ESTILOS------------------------------------------------------------
import styles from './notas.module.css';
import AlertModal from '../../../components/alertModal/alertModal.js';
import SuccessAnimation from './SuccessAnimation.js';

import { useAppContext } from "../../../AppContext.js"; // hook para acessar o estado global do App



function Notes({ data, onArchive, onTrash, onSaveEdit, onTogglePriority, onRefresh, fontSize = 12, visualNotes, filter, onEditing, onEditDoubleclick, onDoubleclickNote }) {

    // ESTADOS E VARIÁVEIS-------------------------------------------------------------------------
    const [notesValue, setNotesValue] = useState(data.notes); // Estado para o valor da nota
    const [listContent, setListContent] = useState(data.listContent || { item: [], status: [] }); // lista de itens da nota
    const [focusIndex, setFocusIndex] = useState(-1); // index do item da lista que recebeu foco
    const [isEditing, setIsEditing] = useState(false); // Estado para controle de edição
    const [isAwaitSave, setIsAwaitSave] = useState(false); // Estado para controle de espera de salvamento
    const [showSuccess, setShowSuccess] = useState(false); // Estado para animação de sucesso
    const [menuOpen, setMenuOpen] = useState(false); // Estado para controle do menu
    const menuRef = useRef(); // referencia para controle do menu de opções das notas
    const menuIcon = useRef(); // referencia para o ícone do menu
    const containerRef = useRef(); // referencia para controle de clicar fora sem salvar nota
    const [alertModal, setAlertModal] = useState({// estado para controlar abertura do modal de alerta
        isOpen: false,
        title: "",
        message: "",
        onConfirm: null,
    });
    const [isScrolled, setIsScrolled] = useState(false); // estado para sombra do cabeçalho
    const textareaRef = useRef(null); // referencia para o textarea principal
    const { t } = useTranslation(); // Hook de tradução
    const { isApp } = useAppContext(); // define se está sendo acessado pelo app ou pelo site



    // obter nota atualizada ao montar o componente ou quando a nota for alterada externamente
    useEffect(() => {
        setNotesValue(data.notes);
        setListContent(data.listContent || { item: [], status: [] });
    }, [data.notes, data.listContent]);



    // FUNÇÕES DIVERSAS----------------------------------------------------------------------------
    // Fecha o menu ao clicar fora
    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target) && menuIcon.current && !menuIcon.current.contains(event.target)) {
                setMenuOpen(false);
            }
        }
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuOpen]);



    // Salva a nota editada
    const handleSave = useCallback(async () => {
        // if (notesValue !== data.notes && notesValue !== '') {
        setIsAwaitSave(true); // mostra icone de carregamento
        try {
            const listToSave = (listContent && listContent.item && listContent.item.length > 0) ? listContent : null;
            await onSaveEdit(data._id, data.date, notesValue, listToSave);
            setIsAwaitSave(false); // oculta icone de carregamento
            setIsEditing(false); // sai do modo edição e oculta o icone salvar
            onEditing(false);
            setShowSuccess(true); // mostra animação de sucesso
            containerRef.current.style.border = 'none'; // reseta a borda de indicação de erro
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setIsAwaitSave(false); // oculta icone de carregamento e mostra o icone salvar
                console.error("Error saving note:", error);
                containerRef.current.style.border = '2px solid red'; // indica erro ao salvar com borda vermelha
                setAlertModal({
                    isOpen: true,
                    title: t("Subscribe to a Plan"),
                    message: t("We were unable to save the note. Subscribe to a plan to continue using the app!"),
                    onConfirm: () => setAlertModal({ ...alertModal, isOpen: false }),
                });
                return;
            }
            if (error.response && error.response.status === 409) {
                setIsAwaitSave(false); // oculta icone de carregamento e mostra o icone salvar
                console.error("Error saving note:", error);
                containerRef.current.style.border = '2px solid red'; // indica erro ao salvar com borda vermelha
                setIsAwaitSave(false); // oculta icone de carregamento
                setIsEditing(false); // sai do modo edição e oculta o icone salvar
                setAlertModal({
                    isOpen: true,
                    title: t("Conflict in Editing"),
                    message: error.response.data.message || t("The note was modified by another device. Please refresh the note to see the latest changes."),
                    onConfirm: () => setAlertModal({ ...alertModal, isOpen: false }),
                });
                return;
            }
            setIsAwaitSave(false); // oculta icone de carregamento e mostra o icone salvar
            console.error("Error saving note:", error);
            containerRef.current.style.border = '1px solid red'; // indica erro ao salvar com borda vermelha
            setAlertModal({
                isOpen: true,
                title: t("Error"),
                message: t("Failed to save the note. Please check your internet connection and try again."),
                onConfirm: () => setAlertModal({ ...alertModal, isOpen: false }),
            });
        }
        // } else {
        //     setNotesValue(data.notes);
        //     setIsEditing(false);
        // }

        // desablita alarme de dependencias
        // eslint-disable-next-line
    }, [notesValue, listContent, data._id, data.notes, onSaveEdit, alertModal, data.date, onEditing]);



    // ao clicar fora da nota editada, verifica se tem conteudo não salvo
    useEffect(() => {
        function handleClickOutside(event) {
            // se o clique aconteceu dentro da nota (.notepadInfos), ignora o efeito
            if (containerRef.current && containerRef.current.contains(event.target)) return;
            if (isEditing) {
                if (notesValue !== data.notes || JSON.stringify(listContent) !== JSON.stringify(data.listContent)) {
                    // se houve alteração, muda a cor da borda
                    containerRef.current.style.border = '2px solid red';
                } else {
                    // se não houve alteração, apenas sai do modo edição
                    containerRef.current.style.border = 'none';
                    setNotesValue(data.notes);
                    setIsEditing(false);
                    onEditing(false);
                }
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isEditing, notesValue, data.notes, handleSave, onEditing, listContent, data.listContent]);



    // Função para mover nota para lixeira
    const handleTrash = useCallback(async () => {
        try {
            setIsEditing(true); // ativa modo de edição
            setIsAwaitSave(true); // mostra icone de carregamento
            await onTrash(data._id, data.date);
            setIsAwaitSave(false); // oculta icone de carregamento
            setIsEditing(false); // sai do modo edição
        } catch (error) {
            if (error.response && error.response.status === 409) {
                setIsAwaitSave(false); // oculta icone de carregamento e mostra o icone salvar
                console.error("Error saving note:", error);
                containerRef.current.style.border = '2px solid red'; // indica erro ao salvar com borda vermelha
                setIsAwaitSave(false); // oculta icone de carregamento
                setIsEditing(false); // sai do modo edição e oculta o icone salvar
                setAlertModal({
                    isOpen: true,
                    title: t("Conflict in Editing"),
                    message: error.response.data.message || t("The note was modified by another device. Please refresh the note to see the latest changes."),
                    onConfirm: () => setAlertModal({ ...alertModal, isOpen: false }),
                });
                return;
            }
            console.error("Error moving note to trash:", error);
            setIsAwaitSave(false); // oculta icone de carregamento
            setIsEditing(false); // sai do modo edição
            setAlertModal({
                isOpen: true,
                title: t("Error"),
                message: t("We were unable to move the note to the trash. Please check your internet connection and try again."),
                onConfirm: () => setAlertModal({ ...alertModal, isOpen: false }),
            });
        }

        // eslint-disable-next-line
    }, [data._id, data.date, onTrash, alertModal]);



    // Função para arquivar ou desarquivar nota
    const handleArchive = useCallback(async () => {
        try {
            setIsEditing(true); // ativa modo de edição
            setIsAwaitSave(true); // mostra icone de carregamento
            await onArchive(data._id, data.date);
            setIsAwaitSave(false); // oculta icone de carregamento
            setIsEditing(false); // sai do modo edição
        } catch (error) {
            if (error.response && error.response.status === 409) {
                setIsAwaitSave(false); // oculta icone de carregamento e mostra o icone salvar
                console.error("Error saving note:", error);
                containerRef.current.style.border = '2px solid red'; // indica erro ao salvar com borda vermelha
                setIsAwaitSave(false); // oculta icone de carregamento
                setIsEditing(false); // sai do modo edição e oculta o icone salvar
                setAlertModal({
                    isOpen: true,
                    title: t("Conflict in Editing"),
                    message: error.response.data.message || t("The note was modified by another device. Please refresh the note to see the latest changes."),
                    onConfirm: () => setAlertModal({ ...alertModal, isOpen: false }),
                });
                return;
            }
            console.error("Error archiving or unarchiving note:", error);
            setIsAwaitSave(false); // oculta icone de carregamento
            setIsEditing(false); // sai do modo edição
            setAlertModal({
                isOpen: true,
                title: t("Error"),
                message: t("We were unable to move or change the file. Please check your internet connection and try again."),
                onConfirm: () => setAlertModal({ ...alertModal, isOpen: false }),
            });
        }

        // eslint-disable-next-line
    }, [data._id, data.date, onArchive, alertModal]);



    // Função para alternar prioridade
    const handleTogglePriority = useCallback(async () => {
        try {
            setIsEditing(true); // ativa modo de edição
            setIsAwaitSave(true); // mostra icone de carregamento
            await onTogglePriority(data._id, data.date);
            setIsAwaitSave(false); // oculta icone de carregamento
            setIsEditing(false); // sai do modo edição
        } catch (error) {
            if (error.response && error.response.status === 409) {
                setIsAwaitSave(false); // oculta icone de carregamento e mostra o icone salvar
                console.error("Error saving note:", error);
                containerRef.current.style.border = '2px solid red'; // indica erro ao salvar com borda vermelha
                setIsAwaitSave(false); // oculta icone de carregamento
                setIsEditing(false); // sai do modo edição e oculta o icone salvar
                setAlertModal({
                    isOpen: true,
                    title: t("Conflict in Editing"),
                    message: error.response.data.message || t("The note was modified by another device. Please refresh the note to see the latest changes."),
                    onConfirm: () => setAlertModal({ ...alertModal, isOpen: false }),
                });
                return;
            }
            console.error("Error changing note priority:", error);
            setIsAwaitSave(false); // oculta icone de carregamento
            setIsEditing(false); // sai do modo edição
            setAlertModal({
                isOpen: true,
                title: t("Error"),
                message: t("We were unable to change the note priority. Please check your internet connection and try again."),
                onConfirm: () => setAlertModal({ ...alertModal, isOpen: false }),
            });
        }

        // eslint-disable-next-line
    }, [data._id, data.date, onTogglePriority, alertModal]);



    // Função para atualizar o conteúdo da nota editada em outro dispositivo
    const handleRefresh = useCallback(async () => {
        try {
            // onRefresh idealmente deve retornar a nota atualizada; aceita também response.data ou { note: {...} }
            const result = await onRefresh(data._id);
            const payload = result && result._id ? result : (result?.note || result);

            // fallback: se onRefresh não retornar nada, tenta usar o objeto `data` atual
            const updatedNotes = payload?.notes ?? data.notes;
            // const updatedListContent = payload?.listContent ?? data.listContent;

            setNotesValue(updatedNotes);
            // setListContent(updatedListContent);
            setIsEditing(false);
            onEditing(false);
            if (containerRef.current) containerRef.current.style.border = 'none';
        } catch (err) {
            console.error('Error updating note (handleRefresh):', err);
        }
    }, [onRefresh, data._id, data.notes, onEditing]);



    // função para criar listas automáticas nas anotações
    function handleKeyUp(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            const txa = e.target;
            const locCursor = txa.selectionStart;
            const textBeforeCursor = notesValue.slice(0, locCursor).trim();

            // Só insere marcador se a frase anterior terminar com ":" ou ";"
            if (textBeforeCursor.endsWith(":") || textBeforeCursor.endsWith(";")) {
                const newValue =
                    notesValue.slice(0, locCursor) + "   ●  " + notesValue.slice(locCursor);
                setNotesValue(newValue);

                // Reposiciona o cursor após o marcador
                setTimeout(() => {
                    txa.selectionStart = txa.selectionEnd = locCursor + 6;
                }, 0);
            }
        }
    }

    // Funções para manipulação da lista
    const addListItem = (index = -1, position = 'after') => {
        setListContent(prev => {
            const newItems = [...prev.item];
            const newStatus = [...prev.status];

            if (index === -1) {
                newItems.push('');
                newStatus.push(false);
            } else {
                const insertIndex = position === 'before' ? index : index + 1;
                newItems.splice(insertIndex, 0, '');
                newStatus.splice(insertIndex, 0, false);
            }

            // Determine the index of the newly added item
            const newFocusIdx = index === -1 ? newItems.length - 1 : (position === 'before' ? index : index + 1);
            // Update focus after list content changes
            setFocusIndex(newFocusIdx);

            return { item: newItems, status: newStatus };
        });

        // Scroll note area to bottom if adding at the end
        if (index === -1) {
            setTimeout(() => {
                const noteArea = document.getElementById('noteAreaScroll');
                if (noteArea) {
                    noteArea.scrollTop = noteArea.scrollHeight;
                }
            }, 50);
        }

    };

    // função para adicionar itens da lista quando o usuário pressionar Enter
    const handleListKeyDown = (e, index) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (e.ctrlKey) {
                addListItem(index, 'before');
                setIsEditing(true);
                onEditing(true);
            } else {
                addListItem(index, 'after');
                setIsEditing(true);
                onEditing(true);
            }
        }
    };

    // função para alterar o status dos itens da lista
    const handleListItemChange = (e, index) => {
        const newStatus = [...listContent.status];
        newStatus[index] = e.target.checked;
        setListContent({ ...listContent, status: newStatus });
    };

    // função para alterar o conteúdo dos itens da lista
    const handleListItemInputChange = (e, index) => {
        const newItems = [...listContent.item];
        newItems[index] = e.target.value;
        setListContent({ ...listContent, item: newItems });
    };

    // função para remover itens da lista
    const handleRemoveListItem = (index) => {
        const newItems = listContent.item.filter((_, i) => i !== index);
        const newStatus = listContent.status.filter((_, i) => i !== index);
        setListContent({ item: newItems, status: newStatus });
    };

    // Efeito para focar no novo item da lista
    useEffect(() => {
        if (focusIndex !== -1 && containerRef.current) {
            const inputs = containerRef.current.getElementsByClassName(styles.listItemInput);
            if (inputs[focusIndex]) {
                inputs[focusIndex].focus();
            }
            setFocusIndex(-1);
        }
    }, [listContent.item, focusIndex]);





    // detecta scroll para sombra no cabeçalho
    const handleScroll = (e) => {
        setIsScrolled(e.target.scrollTop > 0);
    };

    // HTML DO COMPONENTE--------------------------------------------------------------------------
    return (
        <>
            <main ref={containerRef} className={`${styles.notepadInfos} ${styles[data.background]} ${styles[data.colorFont]} ${menuOpen ? styles.menuActive : ''}`} key={data._id} onDoubleClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.target.classList.contains(styles.dobleClick)) {
                    // Remove qualquer seleção de texto que possa ter sido feita
                    window.getSelection().removeAllRanges();
                    filter !== 'arquivo' && !isEditing && onDoubleclickNote && onDoubleclickNote(data);
                }
            }}>

                {/* cabeçalho */}
                {showSuccess ? (
                    <SuccessAnimation onComplete={() => setShowSuccess(false)} />
                ) : (
                    <div className={`${styles.headerContainer} ${isScrolled ? styles.headerScrolled : ''}`}>
                        {/* Título da Nota */}
                        <strong
                            // className={`${data.priority ? styles.titlePriority : ''} ${styles['estiloTexto' + fontType]}`}
                            className={`${styles[data.font]} ${filter === 'arquivo' ? styles.archived : ''} ${filter !== 'arquivo' ? styles[data.colorFont] : ''} ${styles.dobleClick}`}
                            style={
                                {
                                    fontSize: `${fontSize + 4}px`,
                                    fontStyle: data.italicFont,
                                    display: 'block',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                }
                            }
                            title={data.title}
                        >
                            <label className={`${styles.labelTitle} ${styles.dobleClick}`}>
                                {data.title}
                            </label>
                        </strong>

                        {/* Icones de ação */}
                        <div className={styles.noteMenu} ref={menuIcon}>
                            {isEditing ? (
                                <>
                                    {isAwaitSave ? (
                                        // icone de carregamento
                                        <ClipLoader size={20} color={"#48a04a"} loading={isAwaitSave} />
                                    ) : (
                                        // icone para salvar
                                        <MdOutlineSaveAs className={styles.iconSave} onClick={handleSave} title={t("Save")} />
                                    )}
                                </>
                            ) : (
                                // icone do menu
                                <IoEllipsisHorizontal onClick={() => setMenuOpen(!menuOpen)} style={{ cursor: "pointer" }} />
                            )}
                        </div>

                        {/* menu da nota */}
                        {menuOpen && (
                            <div className={styles.dropdownMenu} ref={menuRef}>
                                {onDoubleclickNote && filter !== 'arquivo' && (
                                    <button className={styles.menuItem} onClick={() => { onEditDoubleclick && onEditDoubleclick(data); setMenuOpen(false); }}>
                                        <FiEdit /> {t("Edit")}
                                    </button>
                                )}
                                {onDoubleclickNote && filter !== 'arquivo' && (
                                    <button className={styles.menuItem} onClick={() => { if (!isEditing) { onDoubleclickNote(data); setMenuOpen(false); } }} disabled={isEditing} style={{ opacity: isEditing ? 0.4 : 1, cursor: isEditing ? 'not-allowed' : 'pointer' }}>
                                        <FiMaximize /> {t("expand")}
                                    </button>
                                )}
                                {data.archived ? (
                                    <button className={styles.menuItem} onClick={() => { handleArchive(); setMenuOpen(false); }}>
                                        <GoArchive /> {t("Unarchive")}
                                    </button>
                                ) : (
                                    <button className={styles.menuItem} onClick={() => { handleArchive(); setMenuOpen(false); }}>
                                        <GoArchive /> {t("Archive")}
                                    </button>
                                )}
                                {filter !== 'arquivo' && (
                                    <button className={`${styles.menuItem} ${data.priority ? styles.active : ''}`} onClick={() => { handleTogglePriority(); setMenuOpen(false); }}>
                                        <FiAlertCircle /> {t("Priority")}
                                    </button>
                                )}
                                {filter !== 'arquivo' && (
                                    <button className={styles.menuItem} onClick={() => { handleRefresh(); setMenuOpen(false); }}>
                                        <IoReload /> {t("Refresh")}
                                    </button>
                                )}
                                <button className={styles.menuItem} onClick={() => { handleTrash(); setMenuOpen(false); }}>
                                    <FiTrash /> {t("Delete")}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* conteúdo da nota */}
                <div className={`${styles.utilContent} ${styles[visualNotes]} ${styles.dobleClick}`} onScroll={handleScroll}>
                    <section className={styles.contentSection}>
                        {/* Área de Texto da nota */}
                        <TextareaAutosize
                            ref={textareaRef}
                            className={
                                `${styles.textPcp}
                                ${filter === 'arquivo' ? styles.archived : ''}
                                ${styles[data.font]}
                                ${filter !== 'arquivo' ? styles[data.colorFont] : ''}`
                            }
                            minRows={1}
                            value={notesValue}
                            placeholder={t("Describe your note")}
                            onChange={e => {
                                setNotesValue(e.target.value);
                                setIsEditing(true);
                                onEditing(true);
                            }}
                            onKeyUp={handleKeyUp}
                            style={{ fontSize: `${fontSize}px`, fontWeight: data.fontWeight, fontStyle: data.italicFont, overflow: 'hidden' }} // <-- aplica o font-size
                            readOnly={filter === 'arquivo'}
                            maxLength={5000}
                        />

                        {/* Área de lista de itens da nota */}
                        {listContent && listContent.item && listContent.item.length > 0 && (
                            <div className={`${styles.fieldWrap} ${styles.listContainer}`}>
                                {listContent.item.map((item, index) => (
                                    <div key={index} className={styles.listItem}>
                                        {/* checkbox personalizado com dimensionamento dinâmico */}
                                        <label
                                            className={styles.checkboxContainer}
                                            style={fontSize === 10 ? { width: '14px', height: '14px' } :
                                                fontSize >= 11 && fontSize <= 15 ? { width: '15px', height: '15px' } :
                                                    fontSize === 16 ? { width: '16px', height: '16px' } :
                                                        { width: '18px', height: '18px' }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={listContent.status[index]}
                                                onChange={(e) => {
                                                    if (filter === 'arquivo') return;
                                                    handleListItemChange(e, index);
                                                    setIsEditing(true);
                                                    onEditing(true);
                                                }}
                                                aria-label={t('Mark item as complete')}
                                            />
                                            <span
                                                className={styles.checkmark}
                                                style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    transform: `scale(${fontSize === 10 ? 14 / 20 :
                                                        fontSize >= 11 && fontSize <= 15 ? 15 / 20 :
                                                            fontSize === 16 ? 16 / 20 : 18 / 20})`,
                                                    transformOrigin: 'top left'
                                                }}
                                            ></span>
                                        </label>
                                        <TextareaAutosize
                                            maxLength={500}
                                            minRows={1}
                                            className={`${styles.listItemInput} ${filter === 'arquivo' ? styles.archived : ''} ${filter !== 'arquivo' ? styles[data.colorFont] : ''} ${styles[data.font]}`}
                                            style={{ fontSize: `${fontSize}px`, fontStyle: data.italicFont, textDecoration: listContent.status[index] ? 'line-through' : 'none', opacity: listContent.status[index] ? 0.6 : 1, resize: 'none', overflow: 'hidden' }}
                                            value={item}
                                            readOnly={filter === 'arquivo'}
                                            onChange={(e) => {
                                                handleListItemInputChange(e, index);
                                                setIsEditing(true);
                                                onEditing(true);
                                            }}
                                            onKeyDown={(e) => handleListKeyDown(e, index)}
                                            enterKeyHint="enter" // correção de bug em dispositivos moveis
                                            placeholder={t('Type your item')}
                                            aria-label={t('Item content')}
                                            {...(isApp ? { onDoubleClick: () => { addListItem(index, 'before'); setIsEditing(true); onEditing(true); } } : {})}
                                        />

                                        <button
                                            type="button"
                                            className={styles.listItemRemoveBtn}
                                            onClick={() => {
                                                if (filter === 'arquivo') return;
                                                handleRemoveListItem(index);
                                                setIsEditing(true);
                                                onEditing(true);
                                            }}
                                            aria-label={t('Remove item')}
                                        >
                                            <IoClose size={18} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {data.listContent && (
                            <div className={`${styles.fieldWrap} ${styles.listContainer}`}>
                                <button type="button" className={styles.addListBtn} onClick={() => {
                                    addListItem();
                                    setIsEditing(true);
                                    onEditing(true);
                                }} aria-label={t('Add item to list')}>
                                    <IoAddCircle size={18} />
                                    <span>{t('Add item')}</span>
                                </button>
                            </div>
                        )}
                    </section>
                </div>

                {/* Data da nota */}
                <div className={`${styles.noteDate} ${styles.dobleClick}`}>
                    {/* Símbolo de prioridade */}
                    {data.priority && (
                        <div className={styles.simbolPriority}><FiAlertCircle /></div>
                    )}
                    {/* Data da nota */}
                    {(() => {
                        const noteDate = new Date(data.date);
                        const today = new Date();
                        const isSameDay = noteDate.toDateString() === today.toDateString();
                        return isSameDay ? (
                            <small>{noteDate.toLocaleTimeString()}</small> // somente hora para notas do dia
                        ) : (
                            <small>{noteDate.toLocaleDateString()}</small> // somente data para notas anteriores a hoje
                        );
                    })()}
                </div>
            </main>

            {/* MODAL DE ALERTA */}
            {alertModal.isOpen && (
                <AlertModal
                    isOpen={alertModal.isOpen}
                    onConfirm={alertModal.onConfirm}
                    title={alertModal.title}
                    message={alertModal.message}
                />
            )}
        </>
    )
}

export default Notes;